import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Box, Typography } from '@mui/material';
import { useThemeMode } from '../../../theme/ThemeModeContext';
import { CATEGORICAL, CHART_CHROME } from './chartTokens';

export type TrendDatum = { date: string; income: number; expense: number };

type Props = {
  data: TrendDatum[];
  height?: number;
};

// Income/Expense are categorical identities (not workflow states), so they
// take fixed categorical slots 1/2 rather than status green/red — see the
// dataviz skill's rule that status colors are reserved and never reused
// for a series.
const TrendBarChart = ({ data, height = 280 }: Props) => {
  const { mode } = useThemeMode();
  const [incomeColor, expenseColor] = CATEGORICAL[mode];
  const chrome = CHART_CHROME[mode];
  const surface = mode === 'light' ? '#fcfcfb' : '#1a1a19';

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: 'grid', placeItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">No data yet</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} barGap={2} barCategoryGap="20%">
        <CartesianGrid vertical={false} stroke={chrome.gridline} strokeDasharray="0" />
        <XAxis dataKey="date" tick={{ fill: chrome.mutedText, fontSize: 12 }} axisLine={{ stroke: chrome.axis }} tickLine={false} />
        <YAxis tick={{ fill: chrome.mutedText, fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
        <Tooltip
          cursor={{ fill: mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' }}
          contentStyle={{ background: surface, border: `1px solid ${chrome.gridline}`, borderRadius: 12, fontSize: 13 }}
        />
        <Legend formatter={(value) => <span style={{ color: chrome.mutedText, fontSize: 12 }}>{value}</span>} />
        <Bar dataKey="income" name="Income" fill={incomeColor} radius={[0, 0, 0, 0]} maxBarSize={24} />
        <Bar dataKey="expense" name="Expense" fill={expenseColor} radius={[0, 0, 0, 0]} maxBarSize={24} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default TrendBarChart;
